import React, { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Users, Store, Camera, BadgeCheck, Handshake, MapPin, Hash, RefreshCw, Crown, CreditCard, Sparkles, Share2, Target, Trophy, TrendingUp, Image as ImageIcon } from "lucide-react";
import { PLATFORM_META } from "@/components/SocialIcons";
import { AdminBusinesses } from "@/components/admin/AdminBusinesses";
import { AdminOpportunities } from "@/components/admin/AdminOpportunities";
import { AdminInvestigate } from "@/components/admin/AdminInvestigate";
import { AdminAiGenerations } from "@/components/admin/AdminAiGenerations";
import { AdminCreatorTrials } from "@/components/admin/AdminCreatorTrials";
import { AdminCampaigns } from "@/components/admin/AdminCampaigns";
import { AdminVisits } from "@/components/admin/AdminVisits";
import { AdminReviews } from "@/components/admin/AdminReviews";
import { toast } from "sonner";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "marketplace", label: "Marketplace Impact" },
  { key: "growth", label: "Growth & Referrals" },
  { key: "events", label: "Events" },
  { key: "creators", label: "Creators" },
  { key: "creator-trials", label: "Creator Pro Trials" },
  { key: "email-campaigns", label: "Email Campaigns" },
  { key: "businesses", label: "Businesses" },
  { key: "opportunities", label: "Opportunities" },
  { key: "investigate", label: "Investigate" },
  { key: "collaborations", label: "Collaborations" },
  { key: "visits", label: "Scheduled Visits" },
  { key: "reviews", label: "Reviews" },
  { key: "platforms", label: "Platforms" },
  { key: "billing", label: "Billing" },
  { key: "ai", label: "AI Assist" },
  { key: "ai-generations", label: "AI Generations" },
  { key: "match", label: "Match System" },
  { key: "churn", label: "Account Churn" },
  { key: "settings", label: "Site Settings" },
  { key: "social", label: "Social" },
  { key: "messages", label: "Messages" },
];

const CREATOR_PLANS = [
  { value: "creator_free", label: "Creator Free" },
  { value: "creator_pro", label: "Creator Pro" },
  { value: "lifetime_pro", label: "Founding Lifetime Pro" },
];
const BUSINESS_PLANS = [
  { value: "business_free", label: "Business Free" },
  { value: "business_pro", label: "Business Pro" },
  { value: "founding_business", label: "Founding Business" },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [aiGenBusiness, setAiGenBusiness] = useState("");
  const [investigateQuery, setInvestigateQuery] = useState("");
  const [stats, setStats] = useState(null);
  const [creators, setCreators] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [socialStats, setSocialStats] = useState(null);
  const [socialConns, setSocialConns] = useState([]);
  const [refreshHours, setRefreshHours] = useState(24);
  const [savingSettings, setSavingSettings] = useState(false);
  const [billing, setBilling] = useState(null);
  const [savingBilling, setSavingBilling] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [churn, setChurn] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [events, setEvents] = useState(null);
  const [matchCfg, setMatchCfg] = useState(null);
  const [matchAnalytics, setMatchAnalytics] = useState(null);
  const [savingMatch, setSavingMatch] = useState(false);
  const [impact, setImpact] = useState(null);
  const [aiConfig, setAiConfig] = useState(null);
  const [aiCost, setAiCost] = useState(null);
  const [savingAI, setSavingAI] = useState(false);
  const [savingSP, setSavingSP] = useState(false);
  const [churnFilters, setChurnFilters] = useState({ role: "all", reason: "", start: "", end: "" });
  const [loading, setLoading] = useState(true);

  const loadAiCost = async (month) => {
    const r = await api.get("/admin/ai-cost", { params: month ? { month } : {} });
    setAiCost(r.data);
  };

  const loadChurn = async (f = churnFilters) => {
    const params = {};
    if (f.role && f.role !== "all") params.role = f.role;
    if (f.reason) params.reason = f.reason;
    if (f.start) params.start = f.start;
    if (f.end) params.end = f.end;
    const r = await api.get("/admin/churn", { params });
    setChurn(r.data);
  };

  const loadAll = async () => {
    const [s, cr, bz, co, st, ms, ss, sc, bc, pf, gr, ev, mc, ma, mi, ac] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users", { params: { role: "creator" } }),
      api.get("/admin/users", { params: { role: "business" } }),
      api.get("/collaborations"),
      api.get("/admin/site-settings"),
      api.get("/admin/contact-messages"),
      api.get("/admin/social/stats"),
      api.get("/admin/social/connections"),
      api.get("/admin/billing-config"),
      api.get("/admin/social-platforms"),
      api.get("/admin/growth"),
      api.get("/admin/events"),
      api.get("/admin/match-config"),
      api.get("/admin/match-analytics"),
      api.get("/admin/marketplace-impact"),
      api.get("/admin/ai-config"),
    ]);
    setStats(s.data);
    setCreators(cr.data.users);
    setBusinesses(bz.data.users);
    setCollabs(co.data.collaborations);
    setSettings(st.data);
    setMessages(ms.data.messages);
    setSocialStats(ss.data);
    setSocialConns(sc.data.connections);
    setRefreshHours(ss.data.refresh_hours || 24);
    setBilling(bc.data);
    setPlatforms(pf.data.platforms);
    setGrowth(gr.data);
    setEvents(ev.data);
    setMatchCfg(mc.data);
    setMatchAnalytics(ma.data);
    setImpact(mi.data);
    setAiConfig(ac.data);
    loadAiCost().catch(() => {});
    loadChurn().catch(() => {});
  };

  const saveAI = async (patch) => {
    setSavingAI(true);
    try {
      const r = await api.put("/admin/ai-config", patch);
      setAiConfig((c) => ({ ...r.data, usage: c?.usage || r.data.usage }));
      toast.success("AI settings saved");
    } catch { toast.error("Could not save AI settings"); }
    finally { setSavingAI(false); }
  };

  const saveMatchConfig = async (patch) => {
    setSavingMatch(true);
    try {
      const r = await api.put("/admin/match-config", patch);
      setMatchCfg(r.data);
      toast.success("Match settings saved");
    } catch { toast.error("Could not save match settings"); }
    finally { setSavingMatch(false); }
  };

  const saveSocialProof = async (patch) => {
    setSavingSP(true);
    try {
      const next = { ...(settings.social_proof || {}), ...patch };
      const r = await api.put("/admin/site-settings", { social_proof: next });
      setSettings(r.data);
      toast.success("Social proof counter updated");
    } catch { toast.error("Could not update the counter"); }
    finally { setSavingSP(false); }
  };

  const saveRefreshInterval = async () => {
    try { await api.put("/admin/site-settings", { social_refresh_hours: refreshHours }); toast.success(`Auto-refresh set to every ${refreshHours}h`); loadAll(); }
    catch { toast.error("Could not update refresh interval"); }
  };

  const refreshConnection = async (id) => {
    try { await api.post(`/admin/social/refresh/${id}`); toast.success("Refresh triggered"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await api.put("/admin/site-settings", {
        company_name: settings.company_name, company_number: settings.company_number,
        registered_office: settings.registered_office, contact_email: settings.contact_email,
        support_email: settings.support_email, social: settings.social, policies: settings.policies,
      });
      setSettings(r.data);
      toast.success("Settings saved");
    } catch { toast.error("Could not save settings"); }
    finally { setSavingSettings(false); }
  };

  useEffect(() => { loadAll().finally(() => setLoading(false)); }, []);

  const setBadge = async (userId, field, value) => {
    const r = await api.patch(`/admin/creators/${userId}/badges`, { [field]: value });
    setCreators((cs) => cs.map((c) => (c.id === userId ? { ...c, badges: r.data.badges } : c)));
    toast.success("Updated");
  };

  const setStatus = async (userId, status, list, setList) => {
    await api.patch(`/admin/users/${userId}/status`, { status });
    setList(list.map((u) => (u.id === userId ? { ...u, status } : u)));
    toast.success(status === "suspended" ? "Account suspended" : "Account activated");
  };

  const setPlan = async (userId, plan, list, setList) => {
    await api.patch(`/admin/users/${userId}/plan`, { plan });
    setList(list.map((u) => (u.id === userId ? { ...u, plan } : u)));
    toast.success("Plan updated");
  };

  const setPlanPrice = (key, price) =>
    setBilling((b) => ({ ...b, plans: { ...b.plans, [key]: { ...b.plans[key], price: price === "" ? "" : Number(price) } } }));

  const togglePlatform = async (key, enabled) => {
    const r = await api.patch(`/admin/social-platforms/${key}`, { enabled });
    setPlatforms(r.data.platforms);
    toast.success(`${key} ${enabled ? "enabled" : "disabled"} across Nearfluence`);
  };

  const saveBilling = async () => {
    setSavingBilling(true);
    try {
      const plans = {};
      Object.entries(billing.plans).forEach(([k, v]) => { plans[k] = { price: Number(v.price) || 0, available: v.available }; });
      const r = await api.put("/admin/billing-config", {
        enforcement_enabled: billing.enforcement_enabled,
        payments_enabled: billing.payments_enabled,
        plans,
      });
      setBilling(r.data);
      toast.success("Billing settings saved");
    } catch { toast.error("Could not save billing settings"); }
    finally { setSavingBilling(false); }
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Admin dashboard</h1>
        <p className="mt-1 text-zinc-500">Manage users, verify creators and monitor platform activity.</p>

        <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 border border-zinc-200"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {[
                { label: "Total users", value: stats.total_users, Icon: Users, tint: "text-zinc-600 bg-zinc-200" },
                { label: "Creators", value: stats.creators, Icon: Camera, tint: "text-indigo-600 bg-indigo-100" },
                { label: "Businesses", value: stats.businesses, Icon: Store, tint: "text-emerald-600 bg-emerald-100" },
                { label: "Verified", value: stats.verified_creators, Icon: BadgeCheck, tint: "text-emerald-600 bg-emerald-100" },
                { label: "Collaborations", value: stats.collaborations, Icon: Handshake, tint: "text-amber-600 bg-amber-100" },
                { label: "Suspended", value: stats.suspended, Icon: Users, tint: "text-rose-600 bg-rose-100" },
              ].map((s) => (
                <div key={s.label} data-testid={`admin-stat-${s.label.toLowerCase().replace(/ /g, "-")}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${s.tint}`}><s.Icon className="h-4 w-4" /></div>
                  <p className="mt-2 font-display text-2xl font-bold text-zinc-900">{s.value}</p>
                  <p className="text-xs text-zinc-500">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                <h3 className="flex items-center gap-2 font-display font-bold text-zinc-900"><Hash className="h-4 w-4 text-emerald-600" /> Popular categories</h3>
                <div className="mt-3 space-y-2">
                  {stats.popular_niches.length ? stats.popular_niches.map((n) => (
                    <div key={n.niche} className="flex items-center justify-between text-sm"><span className="text-zinc-600">{n.niche}</span><span className="font-mono font-semibold text-zinc-900">{n.count}</span></div>
                  )) : <p className="text-sm text-zinc-400">No data yet</p>}
                </div>
              </div>
              <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                <h3 className="flex items-center gap-2 font-display font-bold text-zinc-900"><MapPin className="h-4 w-4 text-indigo-600" /> Creator locations</h3>
                <div className="mt-3 space-y-2">
                  {stats.locations.length ? stats.locations.map((l) => (
                    <div key={l.location} className="flex items-center justify-between text-sm"><span className="text-zinc-600">{l.location}</span><span className="font-mono font-semibold text-zinc-900">{l.count}</span></div>
                  )) : <p className="text-sm text-zinc-400">No data yet</p>}
                </div>
              </div>
              <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                <h3 className="flex items-center gap-2 font-display font-bold text-zinc-900">Recent searches</h3>
                <div className="mt-3 space-y-2">
                  {stats.recent_searches.length ? stats.recent_searches.map((s, i) => (
                    <div key={i} className="text-sm text-zinc-600">{[s.q, s.niche, s.location].filter(Boolean).join(" · ") || "—"}</div>
                  )) : <p className="text-sm text-zinc-400">No searches yet</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "marketplace" && impact && (
          <div className="mt-6 space-y-6">
            {/* Funnel */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {[
                { key: "introductions", label: "Introductions", Icon: Users, tint: "text-indigo-600 bg-indigo-100" },
                { key: "applications", label: "Applications", Icon: Share2, tint: "text-sky-600 bg-sky-100" },
                { key: "matches", label: "Matches", Icon: Target, tint: "text-amber-600 bg-amber-100" },
                { key: "collaborations_started", label: "Collaborations Started", Icon: Handshake, tint: "text-emerald-600 bg-emerald-100" },
                { key: "collaborations_completed", label: "Collaborations Completed", Icon: BadgeCheck, tint: "text-emerald-700 bg-emerald-100" },
              ].map((s) => {
                const d = impact.funnel[s.key];
                return (
                  <div key={s.key} data-testid={`mp-funnel-${s.key}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${s.tint}`}><s.Icon className="h-4 w-4" /></div>
                    <p className="mt-2 font-display text-3xl font-bold text-zinc-900" data-testid={`mp-funnel-${s.key}-lifetime`}>{d.lifetime.toLocaleString()}</p>
                    <p className="text-xs font-medium text-zinc-500">{s.label}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
                      <span>30d: <b className="text-zinc-700">{d.last_30_days}</b></span>
                      <span>7d: <b className="text-zinc-700">{d.last_7_days}</b></span>
                      {d.change_30d_pct !== null && (
                        <span className={d.change_30d_pct >= 0 ? "text-emerald-600" : "text-rose-600"}>
                          {d.change_30d_pct >= 0 ? "▲" : "▼"} {Math.abs(d.change_30d_pct)}%
                        </span>
                      )}
                    </div>
                    {d.milestone && <p className="mt-1 text-xs font-semibold text-amber-600">🏆 {d.milestone.toLocaleString()}+ milestone</p>}
                  </div>
                );
              })}
            </div>

            {/* Conversions */}
            <div className="grid gap-4 md:grid-cols-3">
              {[
                { label: "Application → Match", value: impact.conversions.application_to_match },
                { label: "Match → Started", value: impact.conversions.match_to_started },
                { label: "Started → Completed", value: impact.conversions.started_to_completed },
              ].map((c) => (
                <div key={c.label} className="rounded-2xl border border-zinc-200 bg-white p-5">
                  <p className="text-sm font-medium text-zinc-500">{c.label}</p>
                  <p className="mt-1 font-display text-3xl font-bold text-emerald-600">
                    {c.value === null ? <span className="text-zinc-300">—</span> : `${c.value}%`}
                  </p>
                  {c.value === null && <p className="text-xs text-zinc-400">Not enough data yet</p>}
                </div>
              ))}
            </div>

            {/* Supporting stats */}
            <div className="rounded-2xl border border-zinc-200 bg-white p-5">
              <h3 className="flex items-center gap-2 font-display font-bold text-zinc-900"><TrendingUp className="h-4 w-4 text-emerald-600" /> Marketplace health</h3>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                {[
                  ["Active businesses", impact.supporting.active_businesses],
                  ["Active creators", impact.supporting.active_creators],
                  ["Businesses w/ application", impact.supporting.businesses_with_application],
                  ["Creators introduced", impact.supporting.creators_introduced],
                  ["Opps with application", impact.supporting.opportunities_with_application],
                  ["Avg apps / opportunity", impact.supporting.avg_applications_per_opportunity ?? "—"],
                  ["Avg hrs posted→1st app", impact.supporting.avg_hours_posted_to_first_application ?? "—"],
                  ["Avg days started→done", impact.supporting.avg_days_started_to_completed ?? "—"],
                ].map(([label, val]) => (
                  <div key={label}>
                    <p className="font-display text-2xl font-bold text-zinc-900">{val}</p>
                    <p className="text-xs text-zinc-500">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Value (internal only) */}
            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5">
              <h3 className="flex items-center gap-2 font-display font-bold text-zinc-900"><CreditCard className="h-4 w-4 text-amber-600" /> Creator earnings facilitated <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-800">INTERNAL — not public</span></h3>
              <p className="mt-3 font-display text-3xl font-bold text-zinc-900" data-testid="mp-paid-earnings">£{Number(impact.value.paid_earnings_facilitated).toLocaleString()}</p>
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-zinc-600">
                <span>Paid collaborations started: <b>{impact.value.paid_collaborations_started}</b></span>
                <span>Gifted collaborations started: <b>{impact.value.gifted_collaborations_started}</b></span>
              </div>
              <p className="mt-2 text-xs text-zinc-500">{impact.value.note}</p>
            </div>

            {/* Milestones */}
            <div className="rounded-2xl border border-zinc-200 bg-white p-5">
              <h3 className="flex items-center gap-2 font-display font-bold text-zinc-900"><Trophy className="h-4 w-4 text-amber-600" /> Milestones achieved</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {Object.entries(impact.milestones).map(([key, m]) => (
                  <div key={key} data-testid={`mp-milestone-${key}`} className="rounded-xl border border-zinc-100 bg-zinc-50 p-3">
                    <p className="text-xs font-medium capitalize text-zinc-500">{key.replace(/_/g, " ")}</p>
                    <p className="mt-1 font-display text-xl font-bold text-zinc-900">
                      {m.current_milestone ? `${m.current_milestone.toLocaleString()}+` : <span className="text-zinc-300">—</span>}
                    </p>
                    {m.next_milestone && <p className="text-xs text-zinc-400">Next: {m.next_milestone.toLocaleString()} ({m.count}/{m.next_milestone})</p>}
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-zinc-500">Milestones are internal — nothing is published publicly unless you enable the counter below.</p>
            </div>

            {/* Social proof counter config */}
            {settings && (
              <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-2 font-display font-bold text-zinc-900"><Sparkles className="h-4 w-4 text-emerald-600" /> Public Social Proof Counter</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-zinc-500">{settings.social_proof?.enabled ? "ON" : "OFF"}</span>
                    <Switch data-testid="sp-enabled-switch" checked={!!settings.social_proof?.enabled} disabled={savingSP}
                      onCheckedChange={(v) => saveSocialProof({ enabled: v })} />
                  </div>
                </div>
                <p className="mt-1 text-sm text-zinc-500">Off by default. When on, a counter shows on the public homepage — always derived from genuine recorded data.</p>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Metric to display</Label>
                    <Select value={settings.social_proof?.metric || "collaborations_completed"} onValueChange={(v) => saveSocialProof({ metric: v })}>
                      <SelectTrigger data-testid="sp-metric-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="introductions">Introductions</SelectItem>
                        <SelectItem value="matches">Matches</SelectItem>
                        <SelectItem value="collaborations_started">Collaborations Started</SelectItem>
                        <SelectItem value="collaborations_completed">Collaborations Completed</SelectItem>
                        <SelectItem value="creator_earnings">Creator Earnings</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Number display</Label>
                    <Select value={settings.social_proof?.display_mode || "rounded"} onValueChange={(v) => saveSocialProof({ display_mode: v })}>
                      <SelectTrigger data-testid="sp-display-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rounded">Rounded milestone (e.g. 1,000+)</SelectItem>
                        <SelectItem value="exact">Exact number</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4">
                  <Label htmlFor="sp-headline">Custom headline</Label>
                  <Input id="sp-headline" data-testid="sp-headline-input" className="mt-1.5"
                    defaultValue={settings.social_proof?.headline || ""}
                    placeholder="500+ collaborations made possible through Nearfluence"
                    onBlur={(e) => { if (e.target.value !== (settings.social_proof?.headline || "")) saveSocialProof({ headline: e.target.value }); }} />
                  <p className="mt-1 text-xs text-zinc-400">Saved when you click away from the field.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "growth" && growth && (
          <div className="mt-6 space-y-6">
            {/* Funnel stat cards */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {[
                { label: "Invite links", value: growth.invites.links_created, tint: "text-indigo-600 bg-indigo-100" },
                { label: "Invite opens", value: growth.invites.opened, tint: "text-indigo-600 bg-indigo-100" },
                { label: "Signups from invites", value: growth.invites.signups, tint: "text-emerald-600 bg-emerald-100" },
                { label: "Activated referrals", value: growth.invites.activated, tint: "text-emerald-600 bg-emerald-100" },
                { label: "Opportunity shares", value: growth.opportunities.shares, tint: "text-amber-600 bg-amber-100" },
                { label: "Opportunity visits", value: growth.opportunities.visits, tint: "text-amber-600 bg-amber-100" },
                { label: "Signups from opps", value: growth.opportunities.signups, tint: "text-emerald-600 bg-emerald-100" },
                { label: "Apps from shared opps", value: growth.opportunities.applications, tint: "text-emerald-600 bg-emerald-100" },
                { label: "Profile shares", value: growth.profiles.shares, tint: "text-sky-600 bg-sky-100" },
                { label: "Profile visits", value: growth.profiles.visits, tint: "text-sky-600 bg-sky-100" },
                { label: "Signups from profiles", value: growth.profiles.signups, tint: "text-emerald-600 bg-emerald-100" },
                { label: "Connections created", value: growth.connections_created, tint: "text-rose-600 bg-rose-100" },
              ].map((s) => (
                <div key={s.label} data-testid={`growth-stat-${s.label.toLowerCase().replace(/ /g, "-")}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${s.tint}`}><Share2 className="h-4 w-4" /></div>
                  <p className="mt-2 font-display text-2xl font-bold text-zinc-900">{s.value}</p>
                  <p className="text-xs text-zinc-500">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Conversions */}
            <div className="grid gap-4 md:grid-cols-3">
              {[
                { label: "Invite → signup", value: growth.conversions.invite_to_signup, hint: "of opened invite links" },
                { label: "Shared opp → signup", value: growth.conversions.shared_opp_to_signup, hint: "of opportunity visits" },
                { label: "Shared opp → application", value: growth.conversions.shared_opp_to_application, hint: "of opportunity visits" },
              ].map((c) => (
                <div key={c.label} data-testid={`growth-conv-${c.label.toLowerCase().replace(/[ →]+/g, "-")}`} className="rounded-2xl border border-zinc-200 bg-white p-5">
                  <p className="text-sm font-medium text-zinc-500">{c.label}</p>
                  <p className="mt-1 font-display text-3xl font-bold text-emerald-600">{c.value}%</p>
                  <p className="text-xs text-zinc-400">{c.hint}</p>
                </div>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {/* Acquisition by source */}
              <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                <h3 className="flex items-center gap-2 font-display font-bold text-zinc-900"><Sparkles className="h-4 w-4 text-emerald-600" /> Acquisition source</h3>
                <div className="mt-3 space-y-2">
                  {Object.keys(growth.by_source || {}).length ? Object.entries(growth.by_source).sort((a, b) => b[1] - a[1]).map(([src, count]) => (
                    <div key={src} className="flex items-center justify-between text-sm">
                      <span className="capitalize text-zinc-600">{src.replace(/_/g, " ")}</span>
                      <span className="font-mono font-semibold text-zinc-900">{count}</span>
                    </div>
                  )) : <p className="text-sm text-zinc-400">No data yet</p>}
                </div>
              </div>
              {/* Referral activation breakdown */}
              <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                <h3 className="flex items-center gap-2 font-display font-bold text-zinc-900"><BadgeCheck className="h-4 w-4 text-indigo-600" /> Referred-user activation</h3>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-600">Completed profile</span><span className="font-mono font-semibold text-zinc-900">{growth.activation.profile_completed}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600">Connected Instagram</span><span className="font-mono font-semibold text-zinc-900">{growth.activation.connected_instagram}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600">Made first connection</span><span className="font-mono font-semibold text-zinc-900">{growth.activation.first_connection}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600">Applied to first opportunity</span><span className="font-mono font-semibold text-zinc-900">{growth.activation.first_application}</span></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "events" && events && (
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {[{ l: "Events", v: events.counts.events }, { l: "Applications", v: events.counts.applications }, { l: "Accepted", v: events.counts.accepted }].map((c) => (
                <div key={c.l} data-testid={`admin-event-stat-${c.l.toLowerCase()}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                  <p className="font-display text-2xl font-bold text-zinc-900">{c.v}</p><p className="text-xs text-zinc-500">{c.l}</p>
                </div>
              ))}
            </div>
            {events.events.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-zinc-300 bg-white px-4 py-8 text-center text-sm text-zinc-400" data-testid="admin-no-events">No events yet.</p>
            ) : events.events.map((e) => (
              <div key={e.id} data-testid={`admin-event-${e.id}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-fuchsia-600 px-2 py-0.5 text-xs font-bold text-white">EVENT</span>
                  <p className="font-display font-semibold text-zinc-900">{e.title}</p>
                  <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-semibold capitalize text-zinc-600">{(e.event_status || "").replace(/_/g, " ")}</span>
                </div>
                <p className="mt-1 text-sm text-zinc-500">{e.business_name} · {e.venue_name || e.location} · {e.event_date || "—"}</p>
                <p className="mt-1 text-xs text-zinc-500">{e.applicant_count} applicants · {e.accepted_count} accepted · {e.waitlist_count} waitlist · {e.spaces_remaining} left</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" data-testid={`admin-event-view-${e.id}`} onClick={() => window.open(`/opportunity/${e.id}`, "_blank")} className="rounded-full">View</Button>
                  {!e.event_status || (e.event_status !== "cancelled" && e.event_status !== "completed") ? (
                    <Button size="sm" variant="outline" data-testid={`admin-event-cancel-${e.id}`} onClick={async () => { if (window.confirm("Cancel this event?")) { await api.post(`/admin/opportunities/${e.id}/cancel`); toast.success("Event cancelled"); loadAll(); } }} className="rounded-full border-amber-200 text-amber-600 hover:bg-amber-50">Cancel</Button>
                  ) : null}
                  <Button size="sm" variant="outline" data-testid={`admin-event-remove-${e.id}`} onClick={async () => { if (window.confirm("Permanently remove this event and its applications?")) { await api.delete(`/admin/opportunities/${e.id}`); toast.success("Event removed"); loadAll(); } }} className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50">Remove</Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "creators" && (
          <div className="mt-6 space-y-3">
            {creators.map((c) => (
              <div key={c.id} data-testid={`admin-creator-${c.id}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 overflow-hidden rounded-xl bg-zinc-100">
                    {c.profile_photo ? <img src={fileUrl(c.profile_photo)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-display font-bold text-zinc-400">{c.name?.[0]}</div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-semibold text-zinc-900">{c.name}</p>
                    <p className="truncate text-sm text-zinc-500">{c.email} · {c.location || "—"}</p>
                  </div>
                  <Button size="sm" variant={c.status === "suspended" ? "default" : "outline"}
                    data-testid={`admin-suspend-${c.id}`}
                    onClick={() => setStatus(c.id, c.status === "suspended" ? "active" : "suspended", creators, setCreators)}
                    className={c.status === "suspended" ? "rounded-full bg-emerald-600 hover:bg-emerald-700" : "rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"}>
                    {c.status === "suspended" ? "Reactivate" : "Suspend"}
                  </Button>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-zinc-100 pt-3">
                  {["verified", "featured", "founding"].map((b) => (
                    <label key={b} className="flex items-center gap-2 text-sm">
                      <Switch data-testid={`admin-badge-${b}-${c.id}`} checked={!!c.badges?.[b]} onCheckedChange={(v) => setBadge(c.id, b, v)} />
                      <span className="capitalize text-zinc-600">{b}</span>
                    </label>
                  ))}
                  <div className="ml-auto flex items-center gap-2">
                    <Crown className="h-4 w-4 text-amber-500" />
                    <Select value={c.plan || "creator_free"} onValueChange={(v) => setPlan(c.id, v, creators, setCreators)}>
                      <SelectTrigger data-testid={`admin-plan-${c.id}`} className="h-9 w-52"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CREATOR_PLANS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "businesses" && (
          <AdminBusinesses businesses={businesses} setBusinesses={setBusinesses} reload={loadAll} setPlan={setPlan} setStatus={setStatus}
            onViewAiHistory={(bid) => { setAiGenBusiness(bid); setTab("ai-generations"); }} />
        )}

        {tab === "opportunities" && <AdminOpportunities onInvestigate={(query) => { setInvestigateQuery(query); setTab("investigate"); }} />}

        {tab === "investigate" && <AdminInvestigate key={investigateQuery} initialQuery={investigateQuery} />}

        {tab === "visits" && <AdminVisits />}

        {tab === "reviews" && <AdminReviews />}

        {tab === "ai-generations" && <AdminAiGenerations key={aiGenBusiness} initialBusinessId={aiGenBusiness} />}
        {tab === "creator-trials" && <AdminCreatorTrials />}
        {tab === "email-campaigns" && <AdminCampaigns />}

        {tab === "collaborations" && (
          <div className="mt-6 space-y-3">
            {collabs.length === 0 ? <p className="text-sm text-zinc-400">No collaborations yet.</p> : collabs.map((c) => (
              <div key={c.id} className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display font-semibold text-zinc-900">{c.campaign_title}</p>
                  <p className="truncate text-sm text-zinc-500">{c.business_name} → {c.creator_name}</p>
                </div>
                <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-semibold capitalize text-zinc-700">{c.status}</span>
              </div>
            ))}
          </div>
        )}

        {tab === "platforms" && (
          <div className="mt-6 space-y-4">
            <div className="flex items-start gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-800">
              <Share2 className="mt-0.5 h-4 w-4 shrink-0" />
              Control which social platforms are available across Nearfluence. Disabling a platform hides it everywhere (profiles, discovery, opportunities, connections) — existing data is retained and reappears if you re-enable it.
            </div>
            {platforms.map((p) => {
              const meta = PLATFORM_META[p.key] || {};
              const Icon = meta.Icon;
              return (
                <div key={p.key} data-testid={`admin-platform-${p.key}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-4">
                  {Icon && <Icon className={`h-6 w-6 ${meta.color || "text-zinc-500"}`} />}
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-semibold text-zinc-900">{p.name} <span className="ml-1 font-mono text-xs text-zinc-400">{p.key}</span></p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {p.oauth_supported && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">OAuth</span>}
                      {p.manual_entry_supported && <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">Manual entry</span>}
                      {p.analytics_supported && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">Analytics</span>}
                    </div>
                  </div>
                  <span className={`text-xs font-semibold ${p.enabled ? "text-emerald-600" : "text-zinc-400"}`}>{p.enabled ? "Enabled" : "Disabled"}</span>
                  <Switch data-testid={`admin-platform-toggle-${p.key}`} checked={!!p.enabled} onCheckedChange={(v) => togglePlatform(p.key, v)} />
                </div>
              );
            })}
          </div>
        )}

        {tab === "billing" && billing && (
          <div className="mt-6 space-y-5">
            <div className="flex items-start gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-800">
              <CreditCard className="mt-0.5 h-4 w-4 shrink-0" />
              Configure plan prices and control rollout. While <b className="mx-1">enforcement</b> is OFF, all features stay unlocked for everyone (no tester is restricted). Payments are not integrated yet.
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5">
              <h3 className="font-display text-lg font-bold text-zinc-900">Rollout controls</h3>
              <div className="mt-3 space-y-3">
                <label className="flex items-center justify-between gap-3 rounded-xl border border-zinc-100 p-3">
                  <span><span className="font-medium text-zinc-800">Enforce entitlements</span><br /><span className="text-xs text-zinc-500">When ON, Free plans lose Pro features and the Pro visibility boost becomes active.</span></span>
                  <Switch data-testid="billing-enforcement-toggle" checked={!!billing.enforcement_enabled} onCheckedChange={(v) => setBilling((b) => ({ ...b, enforcement_enabled: v }))} />
                </label>
                <label className="flex items-center justify-between gap-3 rounded-xl border border-zinc-100 p-3">
                  <span><span className="font-medium text-zinc-800">Payments enabled</span><br /><span className="text-xs text-zinc-500">When ON, upgrade buttons start a real checkout (requires a payment integration).</span></span>
                  <Switch data-testid="billing-payments-toggle" checked={!!billing.payments_enabled} onCheckedChange={(v) => setBilling((b) => ({ ...b, payments_enabled: v }))} />
                </label>
              </div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5">
              <h3 className="font-display text-lg font-bold text-zinc-900">Plan prices ({billing.currency_symbol || "£"})</h3>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                {["creator_pro", "business_pro"].map((k) => (
                  <div key={k} data-testid={`billing-plan-${k}`}>
                    <Label className="flex items-center gap-1.5">{billing.plans[k]?.label} <span className="text-xs text-zinc-400">/{billing.plans[k]?.interval === "lifetime" ? "lifetime" : "month"}</span></Label>
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="text-zinc-400">{billing.currency_symbol || "£"}</span>
                      <Input data-testid={`billing-price-${k}`} type="number" step="0.01" min="0" value={billing.plans[k]?.price ?? ""} onChange={(e) => setPlanPrice(k, e.target.value)} className="h-10" />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-zinc-400">Free plans are always {billing.currency_symbol || "£"}0. Promotional plans (Founding) are managed per-user in the Creators/Businesses tabs.</p>
            </div>

            <Button data-testid="billing-save-btn" onClick={saveBilling} disabled={savingBilling} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
              {savingBilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-2 h-4 w-4" /> Save billing settings</>}
            </Button>
          </div>
        )}

        {tab === "ai" && aiConfig && (
          <div data-testid="admin-ai-panel" className="mt-6 space-y-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { label: "Generations today", value: aiConfig.usage?.today ?? 0, Icon: Sparkles, tint: "text-emerald-600 bg-emerald-100" },
                { label: "Generations this month", value: aiConfig.usage?.month ?? 0, Icon: TrendingUp, tint: "text-indigo-600 bg-indigo-100" },
                { label: "Unique users (month)", value: aiConfig.usage?.unique_users ?? 0, Icon: Users, tint: "text-amber-600 bg-amber-100" },
              ].map((s) => (
                <div key={s.label} data-testid={`ai-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${s.tint}`}><s.Icon className="h-4 w-4" /></div>
                  <p className="mt-2 font-display text-2xl font-bold text-zinc-900">{s.value}</p>
                  <p className="text-xs text-zinc-500">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5">
              <label className="flex items-center justify-between gap-3">
                <span>
                  <span className="font-display font-bold text-zinc-900">AI Assist</span><br />
                  <span className="text-xs text-zinc-500">Global kill switch. When OFF, no AI requests are made anywhere and users see “AI Assist is temporarily unavailable.”</span>
                </span>
                <Switch data-testid="ai-enabled-toggle" checked={!!aiConfig.enabled} onCheckedChange={(v) => saveAI({ enabled: v })} />
              </label>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5">
              <h3 className="font-display text-lg font-bold text-zinc-900">Daily generation limit</h3>
              <p className="mt-1 text-sm text-zinc-500">Change the per-user daily AI generation allowance without redeploying.</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Free — per user / day</Label>
                  <Input data-testid="ai-daily-limit" type="number" min="0" value={aiConfig.daily_limit ?? 15}
                    onChange={(e) => setAiConfig((c) => ({ ...c, daily_limit: e.target.value }))} className="mt-1.5 h-10" />
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">Nearfluence Pro — per user / day <Crown className="h-3.5 w-3.5 text-amber-500" /></Label>
                  <Input data-testid="ai-pro-daily-limit" type="number" min="0" value={aiConfig.pro_daily_limit ?? 50}
                    onChange={(e) => setAiConfig((c) => ({ ...c, pro_daily_limit: e.target.value }))} className="mt-1.5 h-10" />
                </div>
              </div>
              <p className="mt-3 text-xs text-zinc-400">Pro limits are ready for when subscriptions launch — they don't gate anything today.</p>
              <Button data-testid="ai-save-limits" onClick={() => saveAI({ daily_limit: Number(aiConfig.daily_limit) || 0, pro_daily_limit: Number(aiConfig.pro_daily_limit) || 0 })}
                disabled={savingAI} className="mt-4 rounded-full bg-emerald-600 hover:bg-emerald-700">
                {savingAI ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-2 h-4 w-4" /> Save limits</>}
              </Button>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="ai-image-config">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-lg font-bold text-zinc-900">Image generation</h3>
                  <p className="mt-1 text-sm text-zinc-500">Powers the Business Image Creator. Shares the monthly Pro allowance with copy generation.</p>
                </div>
                <Switch data-testid="ai-image-enabled-toggle" checked={!!aiConfig.image_enabled} onCheckedChange={(v) => saveAI({ image_enabled: v })} />
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div><Label className="text-xs uppercase tracking-wide text-zinc-500">Provider</Label>
                  <Input data-testid="ai-image-provider" value={aiConfig.image_provider ?? "gemini"} onChange={(e) => setAiConfig((c) => ({ ...c, image_provider: e.target.value }))} className="mt-1.5 h-10" /></div>
                <div><Label className="text-xs uppercase tracking-wide text-zinc-500">Model</Label>
                  <Input data-testid="ai-image-model" value={aiConfig.image_model ?? ""} onChange={(e) => setAiConfig((c) => ({ ...c, image_model: e.target.value }))} className="mt-1.5 h-10" /></div>
                <div><Label className="text-xs uppercase tracking-wide text-zinc-500">Quality</Label>
                  <Input data-testid="ai-image-quality" value={aiConfig.image_quality ?? "standard"} onChange={(e) => setAiConfig((c) => ({ ...c, image_quality: e.target.value }))} className="mt-1.5 h-10" /></div>
                <div><Label className="text-xs uppercase tracking-wide text-zinc-500">Est. cost per image (USD)</Label>
                  <Input data-testid="ai-image-cost" type="number" min="0" step="0.001" value={aiConfig.image_cost_per_image_usd ?? 0.04} onChange={(e) => setAiConfig((c) => ({ ...c, image_cost_per_image_usd: e.target.value }))} className="mt-1.5 h-10" /></div>
                <div><Label className="text-xs uppercase tracking-wide text-zinc-500">Monthly spend cap (USD, 0 = none)</Label>
                  <Input data-testid="ai-image-spendcap" type="number" min="0" step="1" value={aiConfig.image_monthly_spend_limit_usd ?? 0} onChange={(e) => setAiConfig((c) => ({ ...c, image_monthly_spend_limit_usd: e.target.value }))} className="mt-1.5 h-10" /></div>
              </div>
              <p className="mt-3 text-xs text-zinc-400">Costs are estimates used for the cost view. Turn the switch off to show businesses an "unavailable" state instead of generating.</p>
              <Button data-testid="ai-image-save" onClick={() => saveAI({ image_provider: aiConfig.image_provider, image_model: aiConfig.image_model, image_quality: aiConfig.image_quality, image_cost_per_image_usd: Number(aiConfig.image_cost_per_image_usd) || 0, image_monthly_spend_limit_usd: Number(aiConfig.image_monthly_spend_limit_usd) || 0 })}
                disabled={savingAI} className="mt-4 rounded-full bg-indigo-600 hover:bg-indigo-700">
                {savingAI ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ImageIcon className="mr-2 h-4 w-4" /> Save image settings</>}
              </Button>
            </div>

            {aiCost && (
              <div data-testid="ai-cost-panel" className="rounded-2xl border border-zinc-200 bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-display text-lg font-bold text-zinc-900">AI Marketing — cost & token usage</h3>
                    <p className="mt-1 text-sm text-zinc-500">Per-generation model, tokens and estimated Emergent spend. Free-tier businesses keep the {aiCost.allowance_per_business}-generation monthly limit.</p>
                  </div>
                  <Select value={aiCost.month} onValueChange={(m) => loadAiCost(m)}>
                    <SelectTrigger data-testid="ai-cost-month" className="h-9 w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(aiCost.months_available || [aiCost.month]).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
                  {[
                    { label: "Generations", value: aiCost.totals.generations, testid: "generations" },
                    { label: "Input tokens", value: aiCost.totals.input_tokens.toLocaleString(), testid: "input-tokens" },
                    { label: "Output tokens", value: aiCost.totals.output_tokens.toLocaleString(), testid: "output-tokens" },
                    { label: "Est. cost (USD)", value: `$${aiCost.totals.est_cost_usd.toFixed(4)}`, testid: "cost-usd" },
                    { label: "Est. credits", value: aiCost.totals.est_credits.toFixed(2), testid: "credits" },
                  ].map((s) => (
                    <div key={s.label} data-testid={`ai-cost-total-${s.testid}`} className="rounded-xl border border-zinc-100 bg-zinc-50 p-3">
                      <p className="font-display text-xl font-bold text-zinc-900">{s.value}</p>
                      <p className="text-xs text-zinc-500">{s.label}</p>
                    </div>
                  ))}
                </div>

                {aiCost.by_type?.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-2" data-testid="ai-cost-by-type">
                    {aiCost.by_type.map((t) => (
                      <div key={t.type} className="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm">
                        <span className="font-semibold capitalize text-zinc-800">{t.type}</span>
                        <span className="ml-2 text-zinc-500">{t.generations} gen · ${t.est_cost_usd.toFixed(4)} · {t.est_credits.toFixed(2)} cr</span>
                      </div>
                    ))}
                  </div>
                )}

                {aiCost.by_model?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">By model</p>
                    <div className="mt-1.5 space-y-1.5">
                      {aiCost.by_model.map((m) => (
                        <div key={m.model} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-zinc-100 px-3 py-2 text-sm">
                          <span className="font-mono font-semibold text-zinc-800">{m.model}</span>
                          <span className="text-zinc-500">{m.generations} gen · {m.input_tokens.toLocaleString()} in / {m.output_tokens.toLocaleString()} out · <b className="text-zinc-800">${m.est_cost_usd.toFixed(4)}</b> · {m.est_credits.toFixed(2)} cr</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm" data-testid="ai-cost-table">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-zinc-400">
                        <th className="py-2 pr-3">Date</th><th className="pr-3">Business</th><th className="pr-3">Type</th>
                        <th className="pr-3">Model</th><th className="pr-3 text-right">Input</th><th className="pr-3 text-right">Output</th>
                        <th className="pr-3 text-right">Est. $</th><th className="text-right">Credits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiCost.generations.length === 0 ? (
                        <tr><td colSpan={8} className="py-6 text-center text-zinc-400" data-testid="ai-cost-empty">No AI marketing generations recorded for this month.</td></tr>
                      ) : aiCost.generations.map((g, i) => (
                        <tr key={i} data-testid={`ai-cost-row-${i}`} className="border-t border-zinc-100">
                          <td className="py-2 pr-3 text-zinc-500">{g.created_at ? new Date(g.created_at).toLocaleDateString() : "—"}</td>
                          <td className="pr-3 font-medium text-zinc-800">{g.business_name}</td>
                          <td className="pr-3 text-zinc-500">{(g.content_type || "").replace(/_/g, " ")}</td>
                          <td className="pr-3 font-mono text-xs text-zinc-600">{g.model}</td>
                          <td className="pr-3 text-right font-mono text-zinc-700">{g.input_tokens.toLocaleString()}</td>
                          <td className="pr-3 text-right font-mono text-zinc-700">{g.output_tokens.toLocaleString()}</td>
                          <td className="pr-3 text-right font-mono text-zinc-900">${g.est_cost_usd.toFixed(5)}</td>
                          <td className="text-right font-mono text-zinc-900">{g.est_credits.toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-xs text-zinc-400">{aiCost.pricing_note}</p>
              </div>
            )}
          </div>
        )}

        {tab === "match" && matchCfg && (
          <div data-testid="admin-match-panel" className="mt-6 space-y-5">
            <div className="flex items-center justify-between rounded-2xl border border-zinc-200 bg-white p-4">
              <div>
                <p className="font-display font-bold text-zinc-900">Match Scores</p>
                <p className="text-sm text-zinc-500">Show compatibility scores to creators and businesses across the app.</p>
              </div>
              <button data-testid="match-enabled-toggle" onClick={() => saveMatchConfig({ enabled: !matchCfg.enabled })}
                className={`relative h-7 w-12 rounded-full transition-colors ${matchCfg.enabled ? "bg-emerald-500" : "bg-zinc-300"}`}>
                <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${matchCfg.enabled ? "left-6" : "left-1"}`} />
              </button>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-4">
              <p className="font-display font-bold text-zinc-900">Weights (%)</p>
              <p className="text-sm text-zinc-500">Total: <b>{Object.values(matchCfg.weights).reduce((a, b) => a + Number(b), 0)}</b> — they're normalised automatically, so any split works.</p>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {Object.keys(matchCfg.weights).map((k) => (
                  <div key={k}>
                    <label className="text-xs font-semibold capitalize text-zinc-500">{k.replace("_", " ")}</label>
                    <input data-testid={`match-weight-${k}`} type="number" min="0" value={matchCfg.weights[k]}
                      onChange={(e) => setMatchCfg((c) => ({ ...c, weights: { ...c.weights, [k]: e.target.value } }))}
                      className="mt-1 block h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm" />
                  </div>
                ))}
              </div>
              <p className="mt-3 font-display font-bold text-zinc-900">Thresholds</p>
              <div className="mt-2 grid grid-cols-3 gap-3">
                {["strong", "good", "potential"].map((k) => (
                  <div key={k}>
                    <label className="text-xs font-semibold capitalize text-zinc-500">{k} ≥</label>
                    <input data-testid={`match-threshold-${k}`} type="number" min="0" max="100" value={matchCfg.thresholds[k]}
                      onChange={(e) => setMatchCfg((c) => ({ ...c, thresholds: { ...c.thresholds, [k]: e.target.value } }))}
                      className="mt-1 block h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm" />
                  </div>
                ))}
              </div>
              <Button data-testid="match-save-btn" onClick={() => saveMatchConfig({ weights: matchCfg.weights, thresholds: matchCfg.thresholds })} disabled={savingMatch} className="mt-4 rounded-full bg-zinc-900 hover:bg-zinc-800">
                {savingMatch ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save weights & thresholds"}
              </Button>
            </div>

            {matchAnalytics && (
              <div className="rounded-2xl border border-zinc-200 bg-white p-4">
                <p className="font-display font-bold text-zinc-900">Match analytics</p>
                <p className="text-sm text-zinc-500">Average match score: <b>{matchAnalytics.average_match_score}%</b> across {matchAnalytics.total_scored_applications} scored application(s)</p>
                <table className="mt-3 w-full text-sm">
                  <thead><tr className="text-left text-zinc-400"><th className="py-1">Band</th><th>Applications</th><th>Accepted</th></tr></thead>
                  <tbody>
                    {["strong", "good", "potential", "low"].map((b) => (
                      <tr key={b} className="border-t border-zinc-100"><td className="py-1.5 font-semibold capitalize">{b}</td><td>{matchAnalytics.by_band[b].applications}</td><td>{matchAnalytics.by_band[b].accepted}</td></tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-[11px] text-zinc-400">Match scores are a recommendation only and never auto-decide applications — businesses always choose.</p>
              </div>
            )}
          </div>
        )}

        {tab === "churn" && (
          <div className="mt-6 space-y-5">
            <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-zinc-200 bg-white p-4">
              <div>
                <label className="text-xs font-semibold text-zinc-500">Account type</label>
                <select data-testid="churn-filter-role" value={churnFilters.role} onChange={(e) => setChurnFilters((f) => ({ ...f, role: e.target.value }))} className="mt-1 block h-9 rounded-lg border border-zinc-200 px-2 text-sm">
                  <option value="all">All</option><option value="creator">Creator</option><option value="business">Business</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-500">Reason</label>
                <input data-testid="churn-filter-reason" value={churnFilters.reason} onChange={(e) => setChurnFilters((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Too expensive" className="mt-1 block h-9 w-48 rounded-lg border border-zinc-200 px-2 text-sm" />
              </div>
              <div><label className="text-xs font-semibold text-zinc-500">From</label><input type="date" data-testid="churn-filter-start" value={churnFilters.start} onChange={(e) => setChurnFilters((f) => ({ ...f, start: e.target.value }))} className="mt-1 block h-9 rounded-lg border border-zinc-200 px-2 text-sm" /></div>
              <div><label className="text-xs font-semibold text-zinc-500">To</label><input type="date" data-testid="churn-filter-end" value={churnFilters.end} onChange={(e) => setChurnFilters((f) => ({ ...f, end: e.target.value }))} className="mt-1 block h-9 rounded-lg border border-zinc-200 px-2 text-sm" /></div>
              <Button data-testid="churn-apply-btn" onClick={() => loadChurn()} className="h-9 rounded-full bg-zinc-900 hover:bg-zinc-800">Apply</Button>
            </div>

            {churn && (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[["Total deletions", churn.total_deletions], ["Total deactivations", churn.total_deactivations], ["Creator deletions", churn.creator_deletions], ["Business deletions", churn.business_deletions], ["Creator deactivations", churn.creator_deactivations], ["Business deactivations", churn.business_deactivations], ["Currently deactivated", churn.currently_deactivated]].map(([l, v]) => (
                    <div key={l} data-testid={`churn-stat-${l}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                      <p className="font-display text-2xl font-bold text-zinc-900">{v}</p>
                      <p className="mt-0.5 text-xs text-zinc-500">{l}</p>
                    </div>
                  ))}
                </div>
                <div className="grid gap-5 lg:grid-cols-2">
                  <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                    <h3 className="font-display text-lg font-bold text-zinc-900">Most common deletion reasons</h3>
                    {churn.reasons.length === 0 ? <p className="mt-2 text-sm text-zinc-500">No deletions recorded for these filters.</p> : (
                      <ul className="mt-3 space-y-2">
                        {churn.reasons.map((r) => (
                          <li key={r.reason} data-testid={`churn-reason-${r.reason}`} className="flex items-center justify-between text-sm"><span className="text-zinc-700">{r.reason}</span><span className="font-mono font-bold text-zinc-900">{r.count}</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                    <h3 className="font-display text-lg font-bold text-zinc-900">Over time (by month)</h3>
                    <p className="mt-2 text-xs font-semibold uppercase text-zinc-400">Deletions</p>
                    <ul className="mt-1 space-y-1">{churn.deletions_over_time.map((m) => <li key={m.month} className="flex justify-between text-sm"><span className="text-zinc-600">{m.month}</span><span className="font-mono font-semibold text-rose-600">{m.count}</span></li>)}{churn.deletions_over_time.length === 0 && <li className="text-sm text-zinc-400">None</li>}</ul>
                    <p className="mt-3 text-xs font-semibold uppercase text-zinc-400">Deactivations</p>
                    <ul className="mt-1 space-y-1">{churn.deactivations_over_time.map((m) => <li key={m.month} className="flex justify-between text-sm"><span className="text-zinc-600">{m.month}</span><span className="font-mono font-semibold text-emerald-600">{m.count}</span></li>)}{churn.deactivations_over_time.length === 0 && <li className="text-sm text-zinc-400">None</li>}</ul>
                  </div>
                </div>
                <p className="text-xs text-zinc-400">Churn analytics are anonymised aggregates — no deleted-user personal data is stored or shown.</p>
              </>
            )}
          </div>
        )}

        {tab === "settings" && settings && (
          <div className="mt-6 space-y-5">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Fields left blank display a <b>REQUIRES FOUNDER REVIEW</b> flag on the public legal pages. Do not enter invented company or contact details — add real values only.
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white p-5">
              <h3 className="font-display font-bold text-zinc-900">Company & contact</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {[
                  ["company_name", "Registered company name"], ["company_number", "Company number"],
                  ["registered_office", "Registered office address"], ["contact_email", "Contact email"],
                  ["support_email", "Support email"],
                ].map(([k, label]) => (
                  <div key={k}><Label>{label}</Label>
                    <Input data-testid={`settings-${k}`} value={settings[k] || ""} onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))} className="mt-1.5" placeholder="Not set" />
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white p-5">
              <h3 className="font-display font-bold text-zinc-900">Social links</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {["instagram", "tiktok", "youtube", "twitter", "facebook", "linkedin"].map((k) => (
                  <div key={k}><Label className="capitalize">{k}</Label>
                    <Input data-testid={`settings-social-${k}`} value={settings.social?.[k] || ""} onChange={(e) => setSettings((s) => ({ ...s, social: { ...s.social, [k]: e.target.value } }))} className="mt-1.5" placeholder="https://…" />
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white p-5">
              <h3 className="font-display font-bold text-zinc-900">Policy versions & dates</h3>
              <div className="mt-4 space-y-3">
                {["terms", "privacy", "cookies"].map((k) => (
                  <div key={k} className="grid grid-cols-3 items-center gap-3">
                    <span className="text-sm font-medium capitalize text-zinc-700">{k}</span>
                    <Input data-testid={`settings-${k}-version`} value={settings.policies?.[k]?.version || ""} onChange={(e) => setSettings((s) => ({ ...s, policies: { ...s.policies, [k]: { ...s.policies[k], version: e.target.value } } }))} placeholder="Version" />
                    <Input data-testid={`settings-${k}-updated`} value={settings.policies?.[k]?.last_updated || ""} onChange={(e) => setSettings((s) => ({ ...s, policies: { ...s.policies, [k]: { ...s.policies[k], last_updated: e.target.value } } }))} placeholder="Last updated" />
                  </div>
                ))}
              </div>
            </div>
            <Button data-testid="settings-save" onClick={saveSettings} disabled={savingSettings} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
              {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
            </Button>
          </div>
        )}

        {tab === "social" && socialStats && (
          <div className="mt-6 space-y-5">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {[
                ["Instagram", socialStats.instagram_connected], ["TikTok", socialStats.tiktok_connected],
                ["YouTube", socialStats.youtube_connected], ["Failures", socialStats.connection_failures],
                ["Revoked", socialStats.revoked], ["Total", socialStats.total],
              ].map(([label, val]) => (
                <div key={label} data-testid={`social-stat-${label.toLowerCase()}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                  <p className="font-display text-2xl font-bold text-zinc-900">{val}</p>
                  <p className="text-xs text-zinc-500">{label}</p>
                </div>
              ))}
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 text-sm text-zinc-600">
              <p><b>Providers configured:</b> {["instagram", "tiktok", "youtube"].map((p) => `${p}: ${socialStats.configured[p] ? "yes" : "no"}`).join(" · ")}</p>
              <p className="mt-1"><b>Last successful sync:</b> {socialStats.last_successful_sync ? new Date(socialStats.last_successful_sync).toLocaleString() : "—"}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-3">
                <span className="font-medium text-zinc-700">Auto-refresh connected stats every</span>
                <select data-testid="refresh-interval-select" value={refreshHours} onChange={(e) => setRefreshHours(Number(e.target.value))}
                  className="rounded-lg border border-zinc-300 px-3 py-1.5 text-sm">
                  {[6, 12, 24, 48, 72].map((h) => <option key={h} value={h}>{h} hours</option>)}
                </select>
                <Button size="sm" data-testid="save-refresh-interval" onClick={saveRefreshInterval} className="rounded-full bg-emerald-600 hover:bg-emerald-700">Save</Button>
              </div>
            </div>
            {socialConns.length === 0 ? (
              <p className="text-sm text-zinc-400">No creators have connected a social account yet. Connections appear here once the Instagram/TikTok apps are approved and creators link their accounts.</p>
            ) : (
              <div className="space-y-2">
                {socialConns.map((c) => (
                  <div key={c.id} data-testid={`social-conn-${c.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-4">
                    <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-semibold capitalize">{c.platform}</span>
                    <span className="font-medium text-zinc-900">@{c.username}</span>
                    <span className="text-xs text-zinc-500">{c.creator_name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${c.sync_status === "ok" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{c.sync_status}</span>
                    {c.api_error && <span className="max-w-xs truncate text-xs text-rose-500">{c.api_error}</span>}
                    <span className="ml-auto text-xs text-zinc-400">{c.last_success_sync ? new Date(c.last_success_sync).toLocaleString() : "never"}</span>
                    <Button size="sm" variant="outline" data-testid={`social-refresh-${c.id}`} onClick={() => refreshConnection(c.id)} className="rounded-full"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {tab === "messages" && (
          <div className="mt-6 space-y-3">
            {messages.length === 0 ? <p className="text-sm text-zinc-400">No contact messages yet.</p> : messages.map((m) => (
              <div key={m.id} data-testid={`contact-msg-${m.id}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-display font-semibold text-zinc-900">{m.name}</p>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">{m.category}</span>
                  <a href={`mailto:${m.email}`} className="text-xs text-zinc-500 hover:underline">{m.email}</a>
                </div>
                <p className="mt-2 whitespace-pre-line text-sm text-zinc-600">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
